import * as React from 'react';

import { type Specialization_Specialization as S12n } from '__generated__/graphql-types-do-not-use';

import type { TrackingData } from 'js/lib/retracked';

import { getProductDifficultyLevelsTranslated } from 'bundles/browse/utils';
import type { S12nTargetDegreesFragment as TargetDegrees } from 'bundles/page-config-common/queries/__generated__/GetS12nTargetDegreesQuery';
import GradientBadge from 'bundles/unified-common/components/recommended-pathway/GradientBadge';
import PathwayCourseProgression from 'bundles/unified-common/components/recommended-pathway/PathwayCourseProgression';
import RecommendedPathwayBody from 'bundles/unified-common/components/recommended-pathway/RecommendedPathwayBody';
import RecommendedPathwayButtons from 'bundles/unified-common/components/recommended-pathway/RecommendedPathwayButtons';
import getPathwayUpsellCaption from 'bundles/unified-common/utils/getPathwayUpsellCaption';
import getProductTitle from 'bundles/unified-common/utils/getProductTitle';

import _t from 'i18n!nls/consumer-home';

export const getRecommendedPathwayProps = (
  trackingData: TrackingData,
  item: S12n,
  itemIndex: number,
  pathways: S12n[],
  role: { name: string; slug: string; id?: string },
  collectionIndex: number,
  targetDegrees?: TargetDegrees,
  nameSize?: 'titleSmall' | 'subtitleLarge' | 'subtitleMedium',
  twoColumnLayout?: boolean
) => {
  const metadata: string[] = [];
  const translatedDifficultyLevel = getProductDifficultyLevelsTranslated(item.difficultyLevel);

  if (translatedDifficultyLevel) {
    metadata.push(translatedDifficultyLevel);
  }
  if (item.monthsToCompleteOverride) {
    metadata.push(_t('#{months} months', { months: item.monthsToCompleteOverride }));
  }
  if (targetDegrees?.items && targetDegrees.items.length > 0) {
    // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    metadata.push(getPathwayUpsellCaption(targetDegrees.items[0]?.stackingType));
  }

  return {
    badge: itemIndex === 0 ? <GradientBadge label={_t('Top recommendation')} /> : undefined,
    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    logo: item.partners[0].squareLogo,
    name: getProductTitle(item.name, item.productVariant, item.slug),
    url: item.urlPath,
    nameSize,
    body: (
      <RecommendedPathwayBody
        skills={
          item?.skillTags?.filter((skill) => Boolean(skill.skillName)).map((skill) => skill.skillName) as string[]
        }
        averageFiveStarRating={item.rating?.averageFiveStarRating}
        ratingCount={item.rating?.ratingCount}
        metadata={metadata}
      />
    ),
    buttons: [
      <RecommendedPathwayButtons
        key={itemIndex}
        item={item}
        itemIndex={itemIndex}
        pathways={pathways}
        role={role}
        trackingData={{
          productCard: {
            index: itemIndex,
            recommender: trackingData.productCard.recommender,
            pageConfigComponent: trackingData.productCard.pageConfigComponent,
          },
          carousel: trackingData.carousel,
        }}
      />,
    ],
    courseProgression: (
      <PathwayCourseProgression
        courseData={item.courses}
        totalCourses={item.courses.length}
        twoColumnLayout={twoColumnLayout}
      />
    ),
    trackingData: {
      productCard: {
        index: itemIndex,
        recommender: trackingData.productCard.recommender,
        pageConfigComponent: trackingData.productCard.pageConfigComponent,
      },
      id: item.id,
      slug: item.slug,
      carousel: {
        ...trackingData.carousel,
        section: collectionIndex,
      },
    },
  };
};
